import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './FecharRodada.css';

/**
 * Página completa para fechar rodada (ao invés de modal)
 */

const FecharRodada = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [rodada, setRodada] = useState(null);
  const [jogadores, setJogadores] = useState([]);
  const [participacoes, setParticipacoes] = useState([]);
  const [jackpotGanhadorId, setJackpotGanhadorId] = useState('');
  const [jackpotGanhou, setJackpotGanhou] = useState(false);
  const [jackpotAtivado, setJackpotAtivado] = useState(false);
  const [custoJanta, setCustoJanta] = useState('');
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [mostrarConsulta, setMostrarConsulta] = useState(false);
  const [dadosConsulta, setDadosConsulta] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/rodadas');
      return;
    }
    carregarDados();
  }, [id]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [rodadasRes, jogadoresRes] = await Promise.all([
        apiClient.get('/rodadas'),
        apiClient.get('/jogadores'),
      ]);
      
      const rodadaEncontrada = rodadasRes.data.find(r => r.id === id);
      if (!rodadaEncontrada) {
        setError('Rodada não encontrada');
        return;
      }

      setRodada(rodadaEncontrada);
      const jogadoresList = Array.isArray(jogadoresRes.data) ? jogadoresRes.data : [];
      // Incluir todos os jogadores ativos OU admins (mesmo se inativos, para permitir adicionar admin na rodada)
      setJogadores(jogadoresList.filter(j => j && (j.ativo || j.is_admin)));

      // IMPORTANTE: Buscar TODAS as participações desta rodada usando o endpoint específico
      // Isso garante que temos TODOS os dados atualizados (confirmações + rebuys + addons + nocautes)
      try {
        const participacoesRes = await apiClient.get(`/rodadas/${id}/participacoes-completas`);
        const participacoesList = Array.isArray(participacoesRes.data) ? participacoesRes.data : [];
        
        if (participacoesList.length > 0) {
          // Mapear todas as participações existentes com seus dados completos
          const participacoesExistentes = participacoesList
            .filter(p => p.jogador_id) // Apenas participações com jogador
            .map((p, index) => ({
              jogador_id: p.jogador_id,
              posicao_final: p.posicao_final || (index + 1), // Se não tem posição (confirmação), usar índice + 1
              entrada_paga: p.entrada_paga !== false,
              buyin_simples: p.buyin_simples ? parseInt(p.buyin_simples) : (p.posicao_final ? 0 : 1), // Se é confirmação, já tem 1 buyin
              buyin_duplo: p.buyin_duplo ? parseInt(p.buyin_duplo) : 0,
              addon: p.addon || false,
              participou_janta: p.participou_janta || false,
              total_saidas: p.total_saidas ? parseFloat(p.total_saidas).toFixed(2) : '',
              nocauteou_ids: Array.isArray(p.nocauteou_ids) ? p.nocauteou_ids : (p.nocauteou_ids ? [p.nocauteou_ids] : []),
              nocauteado_por_id: p.nocauteado_por_id || '',
            }));
          setParticipacoes(participacoesExistentes);
        } else {
          // Se não houver participações, iniciar com uma linha vazia
          setParticipacoes([{
            jogador_id: '',
            posicao_final: 1,
            entrada_paga: true,
            buyin_simples: 0,
            buyin_duplo: 0,
            addon: false,
            participou_janta: false,
            total_saidas: '',
            nocauteou_ids: [],
            nocauteado_por_id: '',
          }]);
        }
      } catch (err) {
        console.error('Erro ao buscar participações completas:', err);
        // Se falhar, tentar buscar confirmações como fallback
        try {
          const confirmacoesRes = await apiClient.get(`/rodadas/${id}/confirmacoes`);
          const confirmacoesList = Array.isArray(confirmacoesRes.data) ? confirmacoesRes.data : [];
          
          if (confirmacoesList.length > 0) {
            const participacoesIniciais = confirmacoesList.map((conf, index) => ({
              jogador_id: conf.jogador_id,
              posicao_final: index + 1,
              entrada_paga: conf.entrada_paga !== false,
              buyin_simples: conf.buyin_simples !== undefined ? parseInt(conf.buyin_simples) : 1,
              buyin_duplo: conf.buyin_duplo ? parseInt(conf.buyin_duplo) : 0,
              addon: conf.addon || false,
              participou_janta: conf.participou_janta || false,
              total_saidas: conf.total_saidas ? parseFloat(conf.total_saidas).toFixed(2) : '',
              nocauteou_ids: Array.isArray(conf.nocauteou_ids) ? conf.nocauteou_ids : [],
              nocauteado_por_id: conf.nocauteado_por_id || '',
            }));
            setParticipacoes(participacoesIniciais);
          } else {
            setParticipacoes([{
              jogador_id: '',
              posicao_final: 1,
              entrada_paga: true,
              buyin_simples: 0,
              buyin_duplo: 0,
              addon: false,
              participou_janta: false,
              total_saidas: '',
              nocauteou_ids: [],
              nocauteado_por_id: '',
            }]);
          }
        } catch (err2) {
          console.error('Erro ao buscar confirmações:', err2);
          setParticipacoes([{
            jogador_id: '',
            posicao_final: 1,
            entrada_paga: true,
            buyin_simples: 0,
            buyin_duplo: 0,
            addon: false,
            participou_janta: false,
            total_saidas: '',
            nocauteou_ids: [],
            nocauteado_por_id: '',
          }]);
        }
      }

      setJackpotAtivado(rodadaEncontrada.jackpot_ativado || false);
      
      if (rodadaEncontrada.jackpot_ganhador_id) {
        setJackpotGanhadorId(rodadaEncontrada.jackpot_ganhador_id);
        setJackpotGanhou(true);
      }
      
      if (rodadaEncontrada.custo_janta) {
        setCustoJanta(parseFloat(rodadaEncontrada.custo_janta).toFixed(2));
      }

      setError('');
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const adicionarParticipacao = () => {
    setParticipacoes([...participacoes, {
      jogador_id: '',
      posicao_final: participacoes.length + 1,
      entrada_paga: true,
      buyin_simples: 0,
      buyin_duplo: 0,
      addon: false,
      participou_janta: false,
      total_saidas: '',
      nocauteou_ids: [],
      nocauteado_por_id: '',
    }]);
  };

  const removerParticipacao = (index) => {
    setParticipacoes(participacoes.filter((_, i) => i !== index));
  };

  const atualizarParticipacao = (index, campo, valor) => {
    const novasParticipacoes = [...participacoes];
    novasParticipacoes[index][campo] = valor;
    
    // Validação: não permitir jogador duplicado
    if (campo === 'jogador_id' && valor) {
      const jogadorJaExiste = novasParticipacoes.some((p, i) => 
        i !== index && p.jogador_id === valor
      );
      if (jogadorJaExiste) {
        setError(`Este jogador já está incluído nesta rodada!`);
        return;
      }
    }
    
    setError(''); // Limpar erro se tudo OK
    setParticipacoes(novasParticipacoes);
  };

  const validarParticipacoes = () => {
    const participacoesValidas = participacoes.filter(p => p.jogador_id);
    if (participacoesValidas.length === 0) {
      setError('Adicione pelo menos um participante');
      return null;
    }

    // Validação: verificar jogadores duplicados
    const jogadoresIds = participacoesValidas.map(p => p.jogador_id);
    const jogadoresUnicos = [...new Set(jogadoresIds)];
    if (jogadoresIds.length !== jogadoresUnicos.length) {
      setError('Um mesmo jogador não pode participar duas vezes na mesma rodada');
      return null;
    }

    // Validação: verificar posições duplicadas
    const posicoes = participacoesValidas.map(p => parseInt(p.posicao_final));
    const posicoesUnicas = [...new Set(posicoes)];
    if (posicoes.length !== posicoesUnicas.length) {
      setError('Não podem existir duas posições iguais na mesma rodada');
      return null;
    }

    return participacoesValidas;
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    console.log('💾 handleSalvar chamado!');
    setError('');
    
    const participacoesValidas = validarParticipacoes();
    if (!participacoesValidas) {
      console.log('❌ Validação falhou');
      return;
    }

    setSalvando(true);

    try {
      const payload = {
        participacoes: participacoesValidas.map(p => ({
          jogador_id: p.jogador_id,
          posicao_final: parseInt(p.posicao_final),
          entrada_paga: p.entrada_paga,
          buyin_simples: parseInt(p.buyin_simples) || 0,
          buyin_duplo: parseInt(p.buyin_duplo) || 0,
          addon: p.addon,
          participou_janta: p.participou_janta,
          total_saidas: p.total_saidas ? Math.round(parseFloat(p.total_saidas) * 100) / 100 : null,
          nocauteou_ids: p.nocauteou_ids || [],
          nocauteado_por_id: p.nocauteado_por_id || null,
        })),
        apenas_salvar: true, // EXPLICITAMENTE true para salvar
      };
      
      console.log('📤 Payload sendo enviado (SALVAR):', JSON.stringify(payload, null, 2));

      // Atualizar status do jackpot
      payload.jackpot_ativado = jackpotAtivado;
      
      if (jackpotAtivado) {
        if (jackpotGanhou && jackpotGanhadorId) {
          payload.jackpot_ganhador_id = jackpotGanhadorId;
        } else {
          payload.jackpot_ganhador_id = null;
        }
      } else {
        payload.jackpot_ganhador_id = null;
      }

      if (custoJanta) {
        payload.custo_janta = parseFloat(custoJanta);
      }

      await apiClient.put(`/rodadas/${id}/fechar`, payload);
      
      alert('Rodada salva com sucesso! Você pode continuar editando.');
      carregarDados();
    } catch (err) {
      console.error('Erro ao salvar rodada:', err);
      setError(err.response?.data?.error || 'Erro ao salvar rodada');
    } finally {
      setSalvando(false);
    }
  };

  const handleConsultar = async () => {
    try {
      setError('');
      const participacoesValidas = validarParticipacoes();
      if (!participacoesValidas || participacoesValidas.length === 0) {
        setError('Adicione pelo menos um participante para consultar');
        return;
      }

      // Calcular valores estimados
      const totalArrecadado = participacoesValidas.reduce((sum, p) => {
        const entrada = p.entrada_paga ? 40 : 0;
        const buyins = (parseInt(p.buyin_simples) || 0) * 25 + (parseInt(p.buyin_duplo) || 0) * 40;
        const addon = p.addon ? 50 : 0;
        const jackpot = jackpotAtivado && (!jackpotGanhou || p.jogador_id !== jackpotGanhadorId) ? 10 : 0;
        const janta = p.participou_janta && custoJanta ? parseFloat(custoJanta) / participacoesValidas.filter(p2 => p2.participou_janta).length : 0;
        return sum + entrada + buyins + addon + jackpot + janta;
      }, 0);

      const caixinha = Math.round((totalArrecadado * 0.3) * 100) / 100;
      const totalPremios = Math.round((totalArrecadado - caixinha) * 100) / 100;

      setDadosConsulta({
        totalArrecadado,
        caixinha,
        totalPremios,
        numParticipantes: participacoesValidas.length,
        participacoes: participacoesValidas.map(p => {
          const jogador = jogadores.find(j => j.id === p.jogador_id);
          const entrada = p.entrada_paga ? 40 : 0;
          const buyins = (parseInt(p.buyin_simples) || 0) * 25 + (parseInt(p.buyin_duplo) || 0) * 40;
          const addon = p.addon ? 50 : 0;
          const jackpot = jackpotAtivado && (!jackpotGanhou || p.jogador_id !== jackpotGanhadorId) ? 10 : 0;
          const janta = p.participou_janta && custoJanta ? parseFloat(custoJanta) / participacoesValidas.filter(p2 => p2.participou_janta).length : 0;
          const totalDebitos = entrada + buyins + addon + jackpot + janta;
          const premio = p.total_saidas ? parseFloat(p.total_saidas) : 0;
          const saldo = premio - totalDebitos;
          
          return {
            jogador: jogador?.nome || 'Desconhecido',
            posicao: p.posicao_final,
            entrada,
            buyins,
            addon,
            jackpot,
            janta: Math.round(janta * 100) / 100,
            totalDebitos: Math.round(totalDebitos * 100) / 100,
            premio: Math.round(premio * 100) / 100,
            saldo: Math.round(saldo * 100) / 100,
          };
        }),
      });
      setMostrarConsulta(true);
    } catch (err) {
      console.error('Erro ao consultar:', err);
      setError('Erro ao calcular consulta');
    }
  };

  const handleExcluirParticipacao = () => {
    if (participacoes.length <= 1) {
      alert('Não é possível excluir a última participação');
      return;
    }

    const indicesParaExcluir = [];
    participacoes.forEach((p, index) => {
      if (p.jogador_id) {
        indicesParaExcluir.push({ index, jogador: jogadores.find(j => j.id === p.jogador_id)?.nome || 'Desconhecido' });
      }
    });

    if (indicesParaExcluir.length === 0) {
      alert('Não há participações para excluir');
      return;
    }

    const mensagem = `Selecione qual participação deseja excluir:\n\n${indicesParaExcluir.map((item, i) => `${i + 1}. ${item.jogador}`).join('\n')}`;
    const escolha = prompt(mensagem + '\n\nDigite o número:');
    const indiceEscolhido = parseInt(escolha) - 1;

    if (indiceEscolhido >= 0 && indiceEscolhido < indicesParaExcluir.length) {
      const indiceReal = indicesParaExcluir[indiceEscolhido].index;
      if (window.confirm(`Deseja realmente excluir a participação de ${indicesParaExcluir[indiceEscolhido].jogador}?`)) {
        removerParticipacao(indiceReal);
      }
    }
  };

  const handleFechar = async (e) => {
    e.preventDefault();
    console.log('🔴 handleFechar chamado!');
    setError('');
    
    // Validar se todas as posições foram lançadas
    const participacoesSemPosicao = participacoes.filter(p => p.jogador_id && !p.posicao_final);
    if (participacoesSemPosicao.length > 0) {
      const jogadoresSemPosicao = participacoesSemPosicao.map(p => {
        const jogador = jogadores.find(j => j.id === p.jogador_id);
        return jogador?.nome || 'Desconhecido';
      }).join(', ');
      
      alert(`⚠️ ATENÇÃO!\n\nNem todos os jogadores têm posição final definida.\n\nJogadores sem posição:\n${jogadoresSemPosicao}\n\nPor favor, lance as posições finais através do menu "Lançar Posição Final" antes de fechar a rodada.`);
      return;
    }
    
    const participacoesValidas = validarParticipacoes();
    if (!participacoesValidas) {
      console.log('❌ Validação falhou');
      return;
    }

    if (!window.confirm('Deseja realmente fechar esta rodada?\n\nApós fechar, será necessário reabrir para fazer alterações.')) {
      console.log('❌ Usuário cancelou');
      return;
    }

    console.log('✅ Prosseguindo com fechamento...');
    setSalvando(true);

    try {
      const payload = {
        participacoes: participacoesValidas.map(p => ({
          jogador_id: p.jogador_id,
          posicao_final: parseInt(p.posicao_final),
          entrada_paga: p.entrada_paga,
          buyin_simples: parseInt(p.buyin_simples) || 0,
          buyin_duplo: parseInt(p.buyin_duplo) || 0,
          addon: p.addon,
          participou_janta: p.participou_janta,
          total_saidas: p.total_saidas ? Math.round(parseFloat(p.total_saidas) * 100) / 100 : null,
          nocauteou_ids: p.nocauteou_ids || [],
          nocauteado_por_id: p.nocauteado_por_id || null,
        })),
        apenas_salvar: false, // EXPLICITAMENTE false para fechar
      };
      
      console.log('📤 Payload sendo enviado:', JSON.stringify(payload, null, 2));

      // Atualizar status do jackpot
      payload.jackpot_ativado = jackpotAtivado;
      
      if (jackpotAtivado) {
        if (jackpotGanhou && jackpotGanhadorId) {
          payload.jackpot_ganhador_id = jackpotGanhadorId;
        } else {
          payload.jackpot_ganhador_id = null;
        }
      } else {
        payload.jackpot_ganhador_id = null;
      }

      if (custoJanta) {
        payload.custo_janta = parseFloat(custoJanta);
      }

      // GARANTIR que apenas_salvar seja explicitamente false - ÚLTIMA coisa antes de enviar
      Object.defineProperty(payload, 'apenas_salvar', {
        value: false,
        writable: false,
        enumerable: true,
        configurable: false
      });
      
      console.log('🔴 FECHAR RODADA - Enviando requisição:', JSON.stringify(payload, null, 2));
      console.log('🔴 apenas_salvar no payload (FINAL):', payload.apenas_salvar, typeof payload.apenas_salvar);
      console.log('🔴 Verificação final - apenas_salvar === false?', payload.apenas_salvar === false);
      
      const response = await apiClient.put(`/rodadas/${id}/fechar`, payload);
      console.log('✅ Resposta recebida:', response.data);
      
      if (response.data && response.data.auditoria) {
        const { auditoria } = response.data;
        const percentualCaixinha = auditoria.percentual_caixinha || 30;
        const percentualPremios = auditoria.percentual_premios || 70;
        alert(`Rodada fechada com sucesso!\n\nTotal arrecadado: R$ ${auditoria.total_arrecadado.toFixed(2)}\nCaixinha (${percentualCaixinha}%): R$ ${auditoria.caixinha.toFixed(2)}\nTotal prêmios (${percentualPremios}%): R$ ${auditoria.total_premios.toFixed(2)}`);
      } else {
        alert('Rodada fechada com sucesso!');
      }
      
      navigate('/rodadas');
    } catch (err) {
      console.error('Erro ao fechar rodada:', err);
      console.error('Detalhes do erro:', err.response?.data);
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao fechar rodada';
      setError(errorMessage);
      alert(`Erro ao fechar rodada: ${errorMessage}`);
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (!rodada) {
    return <div className="container">Rodada não encontrada</div>;
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1>Fechar Rodada - {new Date(rodada.data).toLocaleDateString('pt-BR')}</h1>
        <button className="btn" onClick={() => navigate('/rodadas')}>
          ← Voltar
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      <form onSubmit={(e) => {
        e.preventDefault();
        console.log('⚠️ Form submit interceptado - não deve acontecer');
      }} className="form-fechar-rodada-page">
        <div className="form-section">
          <h3>Informações da Rodada</h3>
          <div className="form-group">
            <label>Custo da Janta (R$)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={custoJanta}
              onChange={(e) => setCustoJanta(e.target.value)}
              placeholder="0.00"
            />
            <small>Valor total da janta que será dividido entre os participantes</small>
          </div>
        </div>

        <div className="form-section">
          <h3>Jackpot</h3>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={jackpotAtivado}
                onChange={(e) => {
                  setJackpotAtivado(e.target.checked);
                  if (!e.target.checked) {
                    setJackpotGanhou(false);
                    setJackpotGanhadorId('');
                  }
                }}
              />
              {' '}Jackpot Ativado
            </label>
            <small>Se marcado, os jogadores pagarão R$ 10,00 cada (exceto o ganhador)</small>
          </div>
          {jackpotAtivado && (
            <>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={jackpotGanhou}
                    onChange={(e) => {
                      setJackpotGanhou(e.target.checked);
                      if (!e.target.checked) {
                        setJackpotGanhadorId('');
                      }
                    }}
                  />
                  {' '}Alguém ganhou o Jackpot
                </label>
              </div>
              {jackpotGanhou && (
                <div className="form-group">
                  <label>Ganhador do Jackpot *</label>
                  <select
                    value={jackpotGanhadorId || ''}
                    onChange={(e) => {
                      console.log('Jackpot ganhador selecionado:', e.target.value);
                      setJackpotGanhadorId(e.target.value);
                    }}
                    required={jackpotGanhou}
                    style={{ 
                      minWidth: '200px',
                      padding: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Selecione o ganhador...</option>
                    {jogadores.map(j => (
                      <option key={j.id} value={j.id}>{j.nome}</option>
                    ))}
                  </select>
                  <small>O ganhador não paga os R$ 10,00 do jackpot</small>
                </div>
              )}
            </>
          )}
        </div>

        <div className="form-section">
          <div className="section-header">
            <h3>Participantes</h3>
          </div>

          {participacoes.map((p, index) => (
            <div key={index} className="participacao-card">
              <div className="participacao-header">
                <h4>Participante #{index + 1}</h4>
                {participacoes.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removerParticipacao(index)}
                  >
                    Remover
                  </button>
                )}
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Jogador *</label>
                  <select
                    value={p.jogador_id}
                    onChange={(e) => atualizarParticipacao(index, 'jogador_id', e.target.value)}
                    required
                    style={{
                      borderColor: participacoes.filter((p2, i) => i !== index && p2.jogador_id === p.jogador_id && p.jogador_id).length > 0 ? '#dc3545' : undefined
                    }}
                  >
                    <option value="">Selecione...</option>
                    {jogadores.map(j => {
                      const jaSelecionado = participacoes.some((p2, i) => i !== index && p2.jogador_id === j.id);
                      return (
                        <option 
                          key={j.id} 
                          value={j.id}
                          disabled={jaSelecionado}
                          style={{
                            backgroundColor: jaSelecionado ? '#f8f9fa' : 'white',
                            color: jaSelecionado ? '#6c757d' : '#333',
                            fontStyle: jaSelecionado ? 'italic' : 'normal'
                          }}
                        >
                          {j.nome}{jaSelecionado ? ' ⚠️ (já incluído na rodada)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="form-group">
                  <label>Posição Final *</label>
                  <input
                    type="number"
                    min="1"
                    value={p.posicao_final}
                    onChange={(e) => atualizarParticipacao(index, 'posicao_final', e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Buy-in Simples</label>
                  <input
                    type="number"
                    min="0"
                    value={p.buyin_simples}
                    onChange={(e) => atualizarParticipacao(index, 'buyin_simples', e.target.value)}
                  />
                  <small>R$ 25,00 cada (já incluído 1 na confirmação = R$ 25,00)</small>
                </div>

                <div className="form-group">
                  <label>Buy-in Duplo</label>
                  <input
                    type="number"
                    min="0"
                    value={p.buyin_duplo}
                    onChange={(e) => atualizarParticipacao(index, 'buyin_duplo', e.target.value)}
                  />
                  <small>R$ 40,00 cada</small>
                </div>

                <div className="form-group">
                  <label>Prêmio Recebido (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={p.total_saidas}
                    onChange={(e) => atualizarParticipacao(index, 'total_saidas', e.target.value)}
                    placeholder="Deixe em branco para cálculo automático"
                  />
                  <small>Será calculado pela posição se deixar em branco</small>
                </div>
              </div>

            </div>
          ))}

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={adicionarParticipacao}>
              + Adicionar Participante
            </button>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn" onClick={() => navigate('/rodadas')} disabled={salvando}>
            Cancelar
          </button>
          <button 
            type="button" 
            className="btn btn-info" 
            onClick={handleConsultar} 
            disabled={salvando}
            style={{ background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)', color: 'white', border: '2px solid #117a8b' }}
          >
            📊 Consultar
          </button>
          <button 
            type="button" 
            className="btn btn-danger" 
            onClick={handleExcluirParticipacao} 
            disabled={salvando}
          >
            🗑️ Excluir Participação
          </button>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('💾 Botão Salvar clicado');
              handleSalvar(e);
            }} 
            disabled={salvando}
          >
            💾 {salvando ? 'Salvando...' : 'Salvar'}
          </button>
          <button 
            type="button"
            className="btn btn-success" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('✅ Botão Fechar Rodada clicado');
              handleFechar(e);
            }} 
            disabled={salvando}
          >
            ✅ {salvando ? 'Fechando...' : 'Fechar Rodada'}
          </button>
        </div>
      </form>

      {/* Modal de Consulta */}
      {mostrarConsulta && dadosConsulta && (
        <div className="modal-overlay" onClick={() => setMostrarConsulta(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>📊 Consulta - Resumo da Rodada</h2>
              <button className="modal-close" onClick={() => setMostrarConsulta(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px' }}>
                <h3>Resumo Geral</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                  <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '8px' }}>
                    <strong>Total Arrecadado</strong>
                    <div style={{ fontSize: '1.5em', color: '#28a745', fontWeight: 'bold' }}>
                      R$ {dadosConsulta.totalArrecadado.toFixed(2)}
                    </div>
                  </div>
                  <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '8px' }}>
                    <strong>Caixinha (30%)</strong>
                    <div style={{ fontSize: '1.5em', color: '#8b0000', fontWeight: 'bold' }}>
                      R$ {dadosConsulta.caixinha.toFixed(2)}
                    </div>
                  </div>
                  <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '8px' }}>
                    <strong>Total Prêmios</strong>
                    <div style={{ fontSize: '1.5em', color: '#007bff', fontWeight: 'bold' }}>
                      R$ {dadosConsulta.totalPremios.toFixed(2)}
                    </div>
                  </div>
                  <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '8px' }}>
                    <strong>Participantes</strong>
                    <div style={{ fontSize: '1.5em', color: '#333', fontWeight: 'bold' }}>
                      {dadosConsulta.numParticipantes}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3>Detalhamento por Jogador</h3>
                <table style={{ width: '100%', marginTop: '10px' }}>
                  <thead>
                    <tr>
                      <th>Jogador</th>
                      <th>Posição</th>
                      <th>Entrada</th>
                      <th>Buy-ins</th>
                      <th>Add-on</th>
                      <th>Jackpot</th>
                      <th>Janta</th>
                      <th>Total Débitos</th>
                      <th>Prêmio</th>
                      <th>Saldo Final</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dadosConsulta.participacoes.map((p, i) => (
                      <tr key={i}>
                        <td>{p.jogador}</td>
                        <td>{p.posicao}</td>
                        <td>R$ {p.entrada.toFixed(2)}</td>
                        <td>R$ {p.buyins.toFixed(2)}</td>
                        <td>{p.addon ? 'R$ 50,00' : '-'}</td>
                        <td>{p.jackpot > 0 ? `R$ ${p.jackpot.toFixed(2)}` : '-'}</td>
                        <td>{p.janta > 0 ? `R$ ${p.janta.toFixed(2)}` : '-'}</td>
                        <td><strong>R$ {p.totalDebitos.toFixed(2)}</strong></td>
                        <td>R$ {p.premio.toFixed(2)}</td>
                        <td style={{ color: p.saldo >= 0 ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                          R$ {p.saldo.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: '20px', textAlign: 'right' }}>
                <button className="btn btn-primary" onClick={() => setMostrarConsulta(false)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FecharRodada;
